import * as yup from 'yup';
import {
  AIRTM_MIN_AGE,
  AIRTM_REGEX,
  CONSECUTIVE_CHARS_REGEX,
  PASSWORD_LENGTH_REGEX,
  PASSWORD_LETTER_REGEX,
  PASSWORD_NUMBER_REGEX,
  PASSWORD_SPECIAL_CHARS_REGEX,
  PHONE_COUNTRY_CODE_REGEX,
  PHONE_REGEX,
  POSTCODE_GBR_REGEX,
  REGEX_FORM_FIELDS,
  REGEX_USERNAME,
  SEQUENTIAL_CHARS_REGEX,
} from '@airtm/utils/dist/constants';
import { BUSINESS, INDIVIDUAL } from '@airtm/utils/dist/constants/accountTypes';
import { subYears } from 'date-fns';
import i18n from 'i18next';
import canHaveBusinessAccount from './canHaveBusinessAccount';
import { validateEmail, validGmailAddress } from './validators';

export const addressLine1 = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_ADDRESS_LINE_1'),
    }),
  )
  .max(60, () =>
    i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_ADDRESS_LINE_1'),
      max: 60,
    }),
  )
  .matches(REGEX_FORM_FIELDS.REGEX_POSTAL_ADDRESS, {
    message: () => i18n.t('SIGNUP:VALIDATION_ADDRESS'),
  });

export const addressLine2 = yup
  .string()
  .notRequired()
  .max(60, () =>
    i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_ADDRESS_LINE_2'),
      max: 60,
    }),
  )
  .matches(REGEX_FORM_FIELDS.REGEX_POSTAL_ADDRESS, {
    message: () => i18n.t('SIGNUP:VALIDATION_ADDRESS'),
    excludeEmptyString: true,
  });

export const birthDate = yup
  .date()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_BIRTH_DATE'),
    }),
  )
  .when('accountType', (accountType, schema) => {
    if (accountType === INDIVIDUAL) {
      return schema.max(subYears(new Date(), AIRTM_MIN_AGE), () =>
        i18n.t('SIGNUP:VALIDATION_MINIMUM_AGE', { minimumAge: AIRTM_MIN_AGE }),
      );
    }
    return schema;
  });

export const city = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SIGNUP:CONFIRMATION_LABEL_CITY') }),
  )
  .max(30, () =>
    i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_CITY'),
      max: 30,
    }),
  )
  .matches(REGEX_FORM_FIELDS.REGEX_POSTAL_ADDRESS, {
    message: () => i18n.t('SIGNUP:VALIDATION_ADDRESS'),
  });

export const country = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SIGNUP:CONFIRMATION_LABEL_COUNTRY') }),
  )
  .test({
    name: 'business-account-validation',
    message: () => i18n.t('SIGNUP:BUSINESS_ACCOUNT_VALIDATION_TITLE'),
    test(value) {
      const accountType = this.resolve(yup.ref('accountType'));
      return canHaveBusinessAccount({ country: value }) || accountType !== BUSINESS;
    },
  });

export const getEmailSchema = (props = {}) => {
  const { fieldName = 'SIGNUP:REQUEST_LABEL_EMAIL', section = '' } = props;

  return yup
    .string()
    .required(() => i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t(fieldName) }))
    .matches(REGEX_FORM_FIELDS.REGEX_EMAIL, () =>
      i18n.t('ERRORS:FORM_REGEX_EMAIL', { name: i18n.t(fieldName) }),
    )
    .test({
      name: 'email-address-validation',
      test(value, ctx) {
        if (value && section === 'signup' && !validGmailAddress(value)) {
          return ctx.createError({
            message: i18n.t('ERRORS:NOT_VALID_USERNAME_EMAIL'),
          });
        }

        return {};
      },
    });
};

export const email = getEmailSchema();

const notContainsAirtm = (value) => {
  return value ? !value.replace(/\s+/g, '').toLowerCase().includes('airtm') : true;
};

export const firstName = yup
  .string()
  .test({
    name: 'not-contains-airtm',
    message: () => i18n.t('ERRORS:INVALID_NAME_AIRTM'),
    test: notContainsAirtm,
  })
  .when('accountType', (accountType, schema) => {
    if (accountType === BUSINESS) {
      return schema
        .required(() =>
          i18n.t('ERRORS:FORM_REQUIRED', {
            name: i18n.t('SIGNUP:CONFIRMATION_LABEL_BUSINESS_NAME'),
          }),
        )
        .min(2, () =>
          i18n.t('ERRORS:FORM_MIN_CHARACTERS', {
            name: i18n.t('SIGNUP:CONFIRMATION_LABEL_BUSINESS_NAME'),
            min: 2,
          }),
        )
        .max(250, () =>
          i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
            name: i18n.t('SIGNUP:CONFIRMATION_LABEL_BUSINESS_NAME'),
            max: 250,
          }),
        )
        .matches(REGEX_FORM_FIELDS.REGEX_BUSINESS_NAME, () =>
          i18n.t('ERRORS:FORM_REGEX_BUSINESS_NAME', {
            name: i18n.t('SIGNUP:CONFIRMATION_LABEL_BUSINESS_NAME'),
          }),
        );
    }

    return schema
      .required(() =>
        i18n.t('ERRORS:FORM_REQUIRED', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_FIRST_NAME'),
        }),
      )
      .matches(REGEX_FORM_FIELDS.REGEX_NAME, () =>
        i18n.t('ERRORS:FORM_REGEX_NAME', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_FIRST_NAME'),
        }),
      )
      .max(250, () =>
        i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_FIRST_NAME'),
          max: 250,
        }),
      );
  });

export const lastName = yup
  .string()
  .test({
    name: 'not-contains-airtm',
    message: () => i18n.t('ERRORS:INVALID_NAME_AIRTM'),
    test: notContainsAirtm,
  })
  .when('accountType', (accountType, schema) => {
    if (accountType === BUSINESS) {
      return schema
        .required(() =>
          i18n.t('ERRORS:FORM_REQUIRED', {
            name: i18n.t('SIGNUP:CONFIRMATION_LABEL_ENTITY_NAME'),
          }),
        )
        .max(250, () =>
          i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
            name: i18n.t('SIGNUP:CONFIRMATION_LABEL_ENTITY_NAME'),
            max: 250,
          }),
        );
    }

    return schema
      .required(() =>
        i18n.t('ERRORS:FORM_REQUIRED', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_LAST_NAME'),
        }),
      )
      .matches(REGEX_FORM_FIELDS.REGEX_NAME, () =>
        i18n.t('ERRORS:FORM_REGEX_NAME', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_LAST_NAME'),
        }),
      )
      .max(250, () =>
        i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_LAST_NAME'),
          max: 250,
        }),
      );
  });

export const lastNameSecond = yup
  .string()
  .test({
    name: 'not-contains-airtm',
    message: () => i18n.t('ERRORS:INVALID_NAME_AIRTM'),
    test: notContainsAirtm,
  })
  .when('accountType', (accountType, schema) => {
    if (accountType === BUSINESS) {
      return schema.notRequired();
    }

    return schema
      .notRequired()
      .matches(REGEX_FORM_FIELDS.REGEX_NAME, () =>
        i18n.t('ERRORS:FORM_REGEX_NAME', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_SECOND_LAST_NAME'),
        }),
      )
      .max(250, () =>
        i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
          name: i18n.t('SIGNUP:CONFIRMATION_LABEL_SECOND_LAST_NAME'),
          max: 250,
        }),
      );
  });

export const phone = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SIGNUP:CONFIRMATION_LABEL_PHONE') }),
  )
  .test({
    name: 'phone-by-country-validation',
    test(value, ctx) {
      const countryPhoneRegex = ctx.resolve(yup.ref('countryPhoneRegex'));
      let isValid = null;
      let defaultValues = { min: 6, max: 15 };

      if (countryPhoneRegex) {
        const phoneRegex = new RegExp(countryPhoneRegex);
        const limits = phoneRegex.toString().match(/\d+/g) ?? [];
        defaultValues =
          limits.length === 1
            ? { number: limits[0] }
            : {
                min: limits[0],
                max: limits[1],
              };
        isValid = value?.match(phoneRegex);
      } else {
        isValid = value?.match(PHONE_REGEX);
      }

      const exactPhoneLengthText = Object.keys(defaultValues).length === 1 ? '_EXACT_LENGTH' : '';

      if (!isValid) {
        return ctx.createError({
          message: i18n.t(`ERRORS:FORM_REGEX_PHONE${exactPhoneLengthText}`, {
            name: i18n.t('FORM_FIELDS:PHONE_NUMBER'),
            ...defaultValues,
          }),
        });
      }

      return {};
    },
  });

export const phoneCountryCode = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_PHONE_COUNTRY_CODE'),
    }),
  )
  .matches(PHONE_COUNTRY_CODE_REGEX);

export const postalCode = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SIGNUP:CONFIRMATION_LABEL_POSTAL_CODE') }),
  )
  .test({
    name: 'postcode-validation',
    message: () => i18n.t('ERRORS:FORM_REGEX_POSTCODE'),
    test(value) {
      const whichCountry = this.resolve(yup.ref('country'));
      return whichCountry === 'GBR'
        ? value?.match(POSTCODE_GBR_REGEX)
        : value?.match(REGEX_FORM_FIELDS.REGEX_POSTAL_ADDRESS);
    },
  });

export const referrer = yup
  .string()
  .notRequired()
  .test({
    message: () => i18n.t('SIGNUP:VALIDATION_USERNAME'),
    name: 'valid-referrer',
    test(value) {
      if (!value) return true;
      return (
        value.match(REGEX_FORM_FIELDS.REGEX_EMAIL) ||
        (value.match(REGEX_USERNAME) && !value.includes('airtm'))
      );
    },
  });

export const state = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SIGNUP:CONFIRMATION_LABEL_STATE') }),
  )
  .max(250, () =>
    i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_STATE'),
      max: 250,
    }),
  )
  .matches(REGEX_FORM_FIELDS.REGEX_POSTAL_ADDRESS, {
    message: () => i18n.t('SIGNUP:VALIDATION_ADDRESS'),
  });

export const password = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SIGNUP:CONFIRMATION_LABEL_PASSWORD') }),
  )
  .test({
    message: () => i18n.t('SIGNUP:VALIDATION_PASSWORD_REGEX'),
    name: 'password-regex',
    test(value) {
      return (
        !!value &&
        value.search(PASSWORD_NUMBER_REGEX) !== -1 &&
        value.search(PASSWORD_LETTER_REGEX) !== -1 &&
        value.search(PASSWORD_SPECIAL_CHARS_REGEX) !== -1 &&
        value.search(PASSWORD_LENGTH_REGEX) !== -1
      );
    },
  })
  .test({
    message: () => i18n.t('SIGNUP:VALIDATION_PASSWORD_NOT_EMAIL'),
    name: 'password-not-email',
    test(value) {
      const emailRef = this.resolve(yup.ref('email'));
      if (!emailRef) {
        return true;
      }
      return !value?.toLowerCase().includes(emailRef.toLowerCase());
    },
  })
  .test({
    message: () => i18n.t('SIGNUP:VALIDATION_PASSWORD_NOT_AIRTM'),
    name: 'password-not-airtm',
    test(value) {
      return value?.search(AIRTM_REGEX) === -1;
    },
  })
  .test({
    message: () => i18n.t('SIGNUP:VALIDATION_PASSWORD_NOT_CONSECUTIVE_IDENTICAL_CHARS'),
    name: 'password-not-consecutive-identical-chars',
    test(value) {
      return value?.search(CONSECUTIVE_CHARS_REGEX) === -1;
    },
  })
  .test({
    message: () => i18n.t('SIGNUP:VALIDATION_PASSWORD_NOT_SEQUENTIAL_CHARS'),
    name: 'password-not-sequential-chars',
    test(value) {
      return value?.search(SEQUENTIAL_CHARS_REGEX) === -1;
    },
  })
  .max(80, () =>
    i18n.t('ERRORS:FORM_MAX_CHARACTERS', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_PASSWORD'),
      max: 80,
    }),
  )
  .min(8, () =>
    i18n.t('ERRORS:FORM_MIN_CHARACTERS', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_PASSWORD'),
      min: 8,
    }),
  );

export const passwordConfirmation = yup
  .string()
  .required(() =>
    i18n.t('ERRORS:FORM_REQUIRED', {
      name: i18n.t('SIGNUP:CONFIRMATION_LABEL_PASSWORD_CONFIRMATION'),
    }),
  )
  .test({
    name: 'password-confirmation-validation',
    message: () => i18n.t('SIGNUP:VALIDATION_PASSWORD_CONFIRM_MATCH'),
    test(value) {
      const passwordValue = this.resolve(yup.ref('password'));
      return value === passwordValue;
    },
  });

export const username = yup
  .string()
  .required(() => i18n.t('ERRORS:FORM_REQUIRED', { name: i18n.t('SETTINGS:USERNAME') }))
  .matches(/^((?!airtm).)*$/, () => i18n.t(`SETTINGS:USERNAME_MODAL_ERROR_AIRTM`))
  .matches(REGEX_USERNAME, () => i18n.t(`SETTINGS:USERNAME_MODAL_ERROR_REGEX`));

const safeCodeRegex = /^[a-zA-Z0-9]*$/;

const utmRegex = /^[\u0020-\u007E\u00A0-\u00FF]*$/;

// Detects highly suspicious SQL command phrases.
const sqlCommands = [
  String.raw`UNION\s+SELECT`,
  String.raw`SELECT\s+.*?\s+FROM`,
  String.raw`INSERT\s+INTO`,
  String.raw`UPDATE\s+\w+\s+SET`,
  String.raw`DELETE\s+FROM`,
  String.raw`DROP\s+TABLE`,
  String.raw`TRUNCATE\s+TABLE`,
  String.raw`ALTER\s+TABLE`,
  String.raw`DROP\s+DATABASE`,
];
const sqlCommandPattern = new RegExp(sqlCommands.join('|'), 'i');

// Define a token pattern for SQL values
const valueToken = /(?:'[^']+'|"[^"]+"|[^\s=]+)/;

// Build tautology pattern using template string
const sqlTautologyPattern = new RegExp(
  `\\b(?:OR|AND)\\s+${valueToken.source}\\s*=\\s*${valueToken.source}\\b`,
  'i',
);

// Detects SQL query terminators and comments.
const sqlTerminatorPattern = /(?:;|--|\/\*|\*\/)/i;

// XSS detection
const xssPattern = /<script.*?>|<\/script>|javascript:/i;

// Command Injection detection
const commandInjectionPattern = /[;&|`]/;

// Path Traversal detection
const pathTraversalPattern = /\.\.\/|\.\.\\|\//;

// NoSQL Injection detection
const noSqlInjectionPattern = /\{\s*"\$.*?":/;

// Unicode Obfuscation detection
const unicodePattern = /%[0-9A-F]{2}/i;

export const urlOrPathString = yup
  .string()
  .matches(/^((https?:\/\/[^\s/$.?#].[^\s]*)|(^\/[^\s]*))$/);

const createPatternTests = (patterns) => {
  return patterns.map(({ name, message, pattern }) =>
    yup.string().test(name, message, (value) => {
      if (!value) return true;
      return !pattern.test(value);
    }),
  );
};

const commonPatterns = [
  {
    name: 'no-sql-commands',
    message: 'Input contains dangerous SQL commands',
    pattern: sqlCommandPattern,
  },
  {
    name: 'no-sql-tautology',
    message: 'Input contains SQL tautology patterns',
    pattern: sqlTautologyPattern,
  },
  {
    name: 'no-sql-terminators',
    message: 'Input contains SQL terminators or comments',
    pattern: sqlTerminatorPattern,
  },
  {
    name: 'no-xss',
    message: 'Code contains malicious scripts',
    pattern: xssPattern,
  },
  {
    name: 'no-command-injection',
    message: 'Code contains command injection patterns',
    pattern: commandInjectionPattern,
  },
  {
    name: 'no-path-traversal',
    message: 'Code contains path traversal patterns',
    pattern: pathTraversalPattern,
  },
  {
    name: 'no-nosql-injection',
    message: 'Code contains NoSQL injection patterns',
    pattern: noSqlInjectionPattern,
  },
  {
    name: 'no-unicode-obfuscation',
    message: 'Code contains encoded characters',
    pattern: unicodePattern,
  },
];

export const safeString = createPatternTests(commonPatterns).reduce(
  (acc, t) => acc.concat(t),
  yup.string(),
);

// reusable validation schema builder
const createSafeStringSchema = (baseRegex, invalidMessage) =>
  safeString.matches(baseRegex, invalidMessage);

export const safeCodeString = yup.string().matches(safeCodeRegex, {
  message: 'Invalid code format',
  excludeEmptyString: true,
});

export const safeUtmString = createSafeStringSchema(utmRegex, 'Invalid utm format');

const base64Regex = /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
export const safeBase64String = yup.string().matches(base64Regex, {
  message: 'Invalid Base64 format',
  excludeEmptyString: true,
});

const categoryIdRegex = /^[a-z0-9:-]*$/;
export const safeCategoryIdString = yup.string().matches(categoryIdRegex, {
  message: 'Invalid Category Id',
  excludeEmptyString: true,
});

const promotionIdRegex = /^[a-zA-Z0-9-]+$/;
export const safePromotionIdString = yup.string().matches(promotionIdRegex, {
  message: 'Invalid Promotion Id',
  excludeEmptyString: true,
});

export const codeOrUsername = yup.string().test({
  name: 'code-or-username',
  message: 'Invalid code or username',
  test(value) {
    if (!value) return true;

    try {
      safeCodeString.validateSync(value);
      return true;
    } catch {
      return REGEX_USERNAME.test(value);
    }
  },
});

export const uuidOrHash = yup.string().test({
  name: 'uuid-or-hash',
  message: 'Must be a valid UUID or hash',
  test(value) {
    if (!value) return true;

    try {
      yup.string().uuid().validateSync(value);
      return true;
    } catch {
      try {
        safeCodeString.validateSync(value);
        return true;
      } catch {
        return false;
      }
    }
  },
});

export const emailOrUsername = yup.string().test({
  name: 'email-or-username',
  message: 'Must be a valid email or username',
  test(value) {
    if (!value) return true;

    if (validateEmail(value)) {
      return true;
    }

    return REGEX_USERNAME.test(value);
  },
});
